import * as util from '../util';
import * as THREE from 'three';
import { DragMode, InteractionMode } from './InteractionHandler';
import { keyCodes } from './KeyboardHandler';
import $ from 'jquery';

export class MouseHandler {
  constructor(imageEngine) {
    this.imageEngine = imageEngine;
    this.isMovingCamera = false;
    this.mouse = {
      x: undefined,
      y: undefined,
    };
    this.isHoveringCanvas = false;
    this._mouseDownTime = undefined;
    this._mouseDownPosition = undefined;
    this.touches = [];
    this.isUsingTouch = false;

    this._mouseDownHandlers = [];
    this._mouseMoveHandlers = [];
    this._mouseUpHandlers = [];

    this._touchStart = this._touchStart.bind(this);
    this._touchMove = this._touchMove.bind(this);
    this._touchEnd = this._touchEnd.bind(this);
    this._mouseDown = this._mouseDown.bind(this);
    this._mouseUp = this._mouseUp.bind(this);
    this._mouseMove = this._mouseMove.bind(this);
    this._mouseScroll = this._mouseScroll.bind(this);
    this._contextMenu = this._contextMenu.bind(this);
  }

  registerEventListeners(container) {
    container.addEventListener('touchstart', this._touchStart);
    window.addEventListener('touchmove', this._touchMove);
    window.addEventListener('touchend', this._touchEnd);
    container.addEventListener('mousedown', this._mouseDown);
    window.addEventListener('mouseup', this._mouseUp);
    window.addEventListener('mousemove', this._mouseMove);
    container.addEventListener('wheel', this._mouseScroll);
    container.addEventListener('contextmenu', this._contextMenu);
  }

  unregisterEventListeners(container) {
    container.removeEventListener('touchstart', this._touchStart);
    window.removeEventListener('touchmove', this._touchMove);
    window.removeEventListener('touchend', this._touchEnd);
    container.removeEventListener('mousedown', this._mouseDown);
    window.removeEventListener('mouseup', this._mouseUp);
    window.removeEventListener('mousemove', this._mouseMove);
    container.removeEventListener('wheel', this._mouseScroll);
    container.removeEventListener('contextmenu', this._contextMenu);
  }

  addMouseDownHandler(handlerFunc) {
    this._mouseDownHandlers.push(handlerFunc);
  }

  removeMouseDownHandler(handlerFunc) {
    this._mouseDownHandlers = this._mouseDownHandlers.filter(
      x => x !== handlerFunc
    );
  }

  addMouseMoveHandler(handlerFunc) {
    this._mouseMoveHandlers.push(handlerFunc);
  }

  removeMouseMoveHandler(handlerFunc) {
    this._mouseMoveHandlers = this._mouseMoveHandlers.filter(
      x => x !== handlerFunc
    );
  }

  addMouseUpHandler(handlerFunc) {
    this._mouseUpHandlers.push(handlerFunc);
  }

  removeMouseUpHandler(handlerFunc) {
    this._mouseUpHandlers = this._mouseUpHandlers.filter(
      x => x !== handlerFunc
    );
  }

  _updateMousePosition(clientX, clientY) {
    const prevX = this.mouse ? this.mouse.x : undefined;
    const prevY = this.mouse ? this.mouse.y : undefined;
    const rect = this.imageEngine.renderer.domElement.getBoundingClientRect();
    this.mouse = {
      x: clientX - rect.left,
      y: clientY - rect.top,
    };
    return {
      dx: prevX === undefined ? 0 : this.mouse.x - prevX,
      dy: prevY === undefined ? 0 : this.mouse.y - prevY,
    };
  }

  _getTouchesDistance() {
    const dx = this.touches[1].x - this.touches[0].x;
    const dy = this.touches[1].y - this.touches[0].y;
    const dist = Math.hypot(dx, dy);
    return dist;
  }

  _updateTouches(touches) {
    const rect = this.imageEngine.renderer.domElement.getBoundingClientRect();

    this.touches = [...touches].map(touch => ({
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    }));
  }

  _touchStart(e) {
    if (!this.imageEngine.renderer) return;

    this.isUsingTouch = true;

    this._updateMousePosition(e.touches[0].clientX, e.touches[0].clientY);
    this._updateTouches(e.touches);
    if (this.touches.length === 1) {
      this._handlePrimaryButtonDown(e.target);
    } else {
      this._mouseDownTime = undefined;
      this._mouseDownPosition = undefined;
    }

    if (e.target === this.imageEngine.renderer.domElement) {
      e.preventDefault();
    }
  }

  _touchMove(e) {
    if (!this.imageEngine.renderer) return;

    if (this.touches.length === 0) {
      return;
    }

    if (this.touches.length === 1) {
      const mouse = this._updateMousePosition(
        e.touches[0].clientX,
        e.touches[0].clientY
      );
      this._handleMovement(mouse.dx, mouse.dy, e.target);
    } else if (this.touches.length === 2) {
      const prevDist = this._getTouchesDistance();
      this._updateTouches(e.touches);
      if (this.touches.length === 2) {
        // If = 1 only one finger was dropped
        const distDelta = this._getTouchesDistance() - prevDist;
        this._handleTouchPinch(distDelta);
      }
    }
  }

  _touchEnd(e) {
    if (!this.imageEngine.renderer) return;

    this._handlePrimaryButtonUp();
    this.mouse = undefined;
  }

  _mouseDown(e) {
    if (!this.imageEngine.renderer) return;

    this.isUsingTouch = false;

    if (e.button === 0) {
      //Left click
      this._handlePrimaryButtonDown(e.target);
    } else if (e.button === 1) {
      //Middle click
      if (e.target === this.imageEngine.renderer.domElement) {
        if (util.debug) {
          this.imageEngine.interactionHandler.rayTestMullionName();
        }
      }
    } else if (e.button === 2) {
      //Right click
      if (e.target === this.imageEngine.renderer.domElement) {
        if (this.imageEngine.enableMeasurement) {
          if (this.imageEngine.enableAllMeasurements) {
            this.imageEngine.interactionHandler.updateAllMeasuredWindows();
          } else {
            this.imageEngine.interactionHandler.updateMeasuredWindow();
          }
        }
      }
    }

    if (e.target === this.imageEngine.renderer.domElement) {
      e.preventDefault();
    }
  }

  _mouseUp(e) {
    if (!this.imageEngine.renderer) return;

    if (e.button === 0) {
      this._handlePrimaryButtonUp();
    }
  }

  _mouseMove(e) {
    if (!this.imageEngine.renderer) return;

    this.isUsingTouch = false;

    const mouse = this._updateMousePosition(e.clientX, e.clientY);
    this._handleMovement(mouse.dx, mouse.dy, e.target);
  }

  _mouseScroll(e) {
    if (!this.imageEngine.renderer) return;

    e.preventDefault();

    if (this.imageEngine.inputHandler.isInputOpen()) {
      this.imageEngine.inputHandler.onMouseScroll(e);
    } else if (e.target == this.imageEngine.renderer.domElement) {
      let speed = 0.1;
      if (this.imageEngine.keyboardHandler.isKeyDown(keyCodes.lshift)) {
        speed /= 10;
      }

      const direction = Math.sign(e.deltaY);
      this._handleZoom(direction * speed);
    }
  }

  _contextMenu(e) {
    if (!this.imageEngine.renderer) return;

    if (e.target === this.imageEngine.renderer.domElement) {
      e.preventDefault();
    }
  }

  _handlePrimaryButtonDown(target) {
    if (target === this.imageEngine.renderer.domElement) {
      this.isMovingCamera = true;
      $(this.imageEngine.container).focus();

      this._mouseDownTime = performance.now();
      this._mouseDownPosition = { ...this.mouse };
    }

    for (const handler of this._mouseDownHandlers) {
      handler(target);
    }
  }

  _handlePrimaryButtonUp() {
    this.imageEngine.requireRenderUpdate = true;
    this.isMovingCamera = false;

    if (
      this._mouseDownTime !== undefined &&
      this._mouseDownPosition !== undefined
    ) {
      const timeThreshold = 500;
      const distThreshold = 10;

      const now = performance.now();
      const shouldClick =
        now - this._mouseDownTime <= timeThreshold &&
        Math.abs(this.mouse.x - this._mouseDownPosition.x) <= distThreshold &&
        Math.abs(this.mouse.y - this._mouseDownPosition.y) <= distThreshold;
      if (shouldClick) {
        this._handlePrimaryButtonClick();
      }
    }

    this._mouseDownPosition = undefined;
    this._mouseDownTime = undefined;

    for (const handler of this._mouseUpHandlers) {
      handler();
    }
  }

  _handlePrimaryButtonClick() {
    if (
      this.imageEngine.interactionHandler.interactionMode !==
      InteractionMode.imageMode
    ) {
      this.imageEngine.interactionHandler.rayTest(true);
    }
  }

  _handleMovement(dx, dy, target) {
    if (this.isMovingCamera) {
      const interactionHandler = this.imageEngine.interactionHandler;
      const dragMode = interactionHandler.dragMode;
      if (dragMode === DragMode.rotate) {
        if (
          interactionHandler.enabledRotate === true ||
          (interactionHandler.enabledRotate !== false &&
            interactionHandler.interactionMode !== InteractionMode.imageMode)
        ) {
          const rotateSensitivity = 0.007;
          this.imageEngine.camera.rotateCamera(
            -dx * rotateSensitivity,
            -dy * rotateSensitivity
          );
          this.imageEngine.requireRenderUpdate = true;
        }
      } else if (dragMode === DragMode.pan) {
        if (interactionHandler.interactionMode !== InteractionMode.imageMode) {
          const cameraDist = this.imageEngine.camera.getCameraDistance();
          const rendererHeight = this.imageEngine.renderer.getSize(
            new THREE.Vector2()
          ).height;
          const panSensitivity = 0.0012 * cameraDist * (1000 / rendererHeight);
          this.imageEngine.camera.panCamera(-dx * panSensitivity, 0);
          this.imageEngine.requireRenderUpdate = true;
        }
      }
    }

    const newIsHoveringCanvas = target === this.imageEngine.renderer.domElement;
    if (this.isHoveringCanvas !== newIsHoveringCanvas) {
      this.isHoveringCanvas = newIsHoveringCanvas;
      this.imageEngine.requireRenderUpdate = true;
    }

    if (this.isHoveringCanvas) {
      this.imageEngine.requireRenderUpdate = true;
    }

    for (const handler of this._mouseMoveHandlers) {
      handler(dx, dy);
    }
  }

  _handleTouchPinch(distDelta) {
    const sensitivity = 0.002 * window.devicePixelRatio;
    this._handleZoom(-distDelta * sensitivity);
  }

  _handleZoom(distance) {
    const interactionHandler = this.imageEngine.interactionHandler;
    if (
      interactionHandler.enabledZoom === true ||
      (interactionHandler.enabledZoom !== false &&
        interactionHandler.interactionMode !== InteractionMode.imageMode)
    ) {
      this.imageEngine.camera.zoomCamera(distance);
      this.imageEngine.requireRenderUpdate = true;
    }
  }
}
